import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Box, Typography, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { User, Users } from 'lucide-react';
import SlateEditor from '@common-components/tasks/extraData/lazy-slate';
import { WaNote } from '@common-models/WaNote';
import { selectUserId } from '@common-reducers/UserSelectors';
import { createWaNoteThunk } from '@common-reducers/wa-notes-thunks';
import NotesLazyLoadingVirtuoso from './notes-lazy-loading-virtuoso';
import { useTranslation } from 'react-i18next';
import { namespace } from "@common-config/constants";

type ViewMode = 'current' | 'all';

export default function NotesPanel() {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const currentChat = useAppSelector((state) => state.WhatsAppReducer.currentChat);
    const chatId = currentChat?.id?._serialized;
    const userId = useAppSelector(selectUserId);

    const [viewMode, setViewMode] = useState<ViewMode>('current');
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    const handleCreateNote = async (editorValue: any) => {
        if (!chatId || isCreating) return;

        const newNote: WaNote = {
            chatId,
            extraData: editorValue.editorValue,
            owner: userId,
            createdAt: new Date().toISOString(),
        };

        setIsCreating(true);
        try {
            await dispatch(createWaNoteThunk({ note: newNote })).unwrap();
            setIsEditing(false);
        } catch (error) {
            console.error('[notes-panel] Failed to create note:', error);
        } finally {
            setIsCreating(false);
        }
    };

    // View Mode Toggle component
    const ViewModeToggle = () => (
        <Box sx={{ p: 1.5, borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                sx={{
                    '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        px: 2,
                        py: 0.5,
                        fontSize: '0.8rem'
                    }
                }}
            >
                <ToggleButton value="current" aria-label="current contact">
                    <User size={14} style={{ marginRight: 6 }} />
                    {t('notes.current')}
                </ToggleButton>
                <ToggleButton value="all" aria-label="all notes">
                    <Users size={14} style={{ marginRight: 6 }} />
                    {t('notes.allNotes')}
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );

    // When no chat is selected and viewing current mode
    if (!chatId && viewMode === 'current') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <ViewModeToggle />
                <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                        {t('notes.selectChatToViewNotes')}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <ViewModeToggle />

            {/* New Note Section - only show when viewing current contact */}
            {viewMode === 'current' && chatId && (
                <Box sx={{ p: 2, borderBottom: '1px solid var(--color-border)' }}>
                   {isEditing ? (
                      <SlateEditor
                        value={null}
                        onValueUpdate={handleCreateNote}
                        addBorder={true}
                        id={`wa_note_new_${chatId}`}
                        isDraft={true}
                        isLoading={isCreating}
                      />
                   ) : (
                      <Button variant="outlined" fullWidth onClick={() => setIsEditing(true)}>
                          {t('notes.takeANote')}
                      </Button>
                   )}
                </Box>
            )}

            {/* Notes List */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <NotesLazyLoadingVirtuoso
                    viewMode={viewMode}
                    chatId={chatId}
                    emptyMessage={viewMode === 'all' ? t('notes.noNotesYet') : t('notes.noNotesForContact')}
                />
            </Box>
        </Box>
    );
}
